import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Banknote, Briefcase, Users, FileText, Send, Calendar, X, Clock,
  ChevronDown, ChevronUp, Plus, Trash2, BookOpen, TrendingDown,
  Wallet, CheckCircle, BarChart2, AlertTriangle, User, CheckSquare, Square
} from 'lucide-react';
import { getAuthHeader } from "../../helpers/auth";

const API = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────
// ✅ TIMEZONE-SAFE DATE HELPER
// ─────────────────────────────────────────────────────────────
const toLocalDateString = (rawDate) => {
  if (!rawDate) return '';
  // Agar ISO format hai (contains 'T' or 'Z'), new Date() se local date nikalo
  if (String(rawDate).includes('T') || String(rawDate).includes('Z')) {
    const d = new Date(rawDate);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  // Plain date string "2025-05-02" — seedha substring
  return String(rawDate).substring(0, 10);
};

// ─────────────────────────────────────────────────────────────
// SHARED MINI STYLES
// ─────────────────────────────────────────────────────────────
const addInputStyle = {
  width: '100%', height: 34, background: '#141927',
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7,
  color: '#E2E8F0', fontSize: 12, padding: '0 10px',
  outline: 'none', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};

const addBtnStyle = (color, bg) => ({
  height: 34, padding: '0 12px', borderRadius: 7,
  background: bg, border: `1px solid ${color}40`,
  color, fontSize: 11, fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
  fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
});

// ─────────────────────────────────────────────────────────────
// LEDGER PANEL  — DB backed
// ─────────────────────────────────────────────────────────────
const LedgerPanel = ({ req, onLedgerUpdate }) => {
  const [open, setOpen]       = useState(false);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_received: 0, total_spent: 0, remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', amount: '' });
  const [adding, setAdding]   = useState(false);
  const [saving, setSaving]   = useState(false);

  const isApproved = req.status?.toLowerCase() === 'approved';

  useEffect(() => {
    if (!isApproved || !open) return;
    fetchLedger();
  }, [open, isApproved]);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/job-orders/finance/ledger/${req.id}`);
      if (res.data.success) {
        setEntries(res.data.data.entries);
        setSummary({
          total_received: res.data.data.total_received,
          total_spent:    res.data.data.total_spent,
          remaining:      res.data.data.remaining,
        });
      }
    } catch (err) {
      console.error('Ledger fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isApproved) return null;

  const addExpense = async () => {
    if (!newItem.description.trim() || !newItem.amount) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/job-orders/finance/ledger`, {
        request_id:  req.id,
        description: newItem.description,
        amount:      newItem.amount,
        added_by:    'team',
      });
      if (res.data.success) {
        setNewItem({ description: '', amount: '' });
        setAdding(false);
        await fetchLedger();
        if (onLedgerUpdate) onLedgerUpdate();
      }
    } catch (err) {
      console.error('Ledger save failed:', err.message);
      alert('Failed to save expense. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const removeExpense = async (entryId) => {
    try {
      await axios.delete(`${API}/job-orders/finance/ledger/${entryId}`);
      await fetchLedger();
      if (onLedgerUpdate) onLedgerUpdate();
    } catch (err) {
      console.error('Delete failed:', err.message);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const pct      = summary.total_received > 0
    ? Math.min((summary.total_spent / summary.total_received) * 100, 100) : 0;
  const pctColor = pct > 90 ? '#F87171' : pct > 65 ? '#FBBF24' : '#22D3A5';

  return (
    <div style={{ marginTop: 10 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: open ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.28)',
          borderRadius: 8, padding: '6px 13px',
          color: '#60A5FA', fontSize: 11, fontWeight: 700,
          cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          transition: 'all 0.18s',
        }}
      >
        <BookOpen size={13} />
        Team Ledger
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {entries.length > 0 && (
          <span style={{ background: 'rgba(59,130,246,0.35)', borderRadius: 4, padding: '1px 7px', fontSize: 10 }}>
            {entries.length} entries
          </span>
        )}
        {!open && summary.total_spent > 0 && (
          <span style={{ marginLeft: 4, fontSize: 10, color: '#FBBF24', fontFamily: "'DM Mono', monospace" }}>
            ₨{parseFloat(summary.total_spent).toLocaleString()} spent
          </span>
        )}
      </button>

      {open && (
        <div style={{
          marginTop: 8, background: '#06091A',
          border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 12, overflow: 'hidden',
          animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              { label: 'Approved',  val: summary.total_received, color: '#22D3A5', icon: <Wallet size={12} /> },
              { label: 'Spent',     val: summary.total_spent,    color: '#FBBF24', icon: <TrendingDown size={12} /> },
              { label: 'Remaining', val: summary.remaining,      color: summary.remaining < 0 ? '#F87171' : '#60A5FA', icon: <BarChart2 size={12} /> },
            ].map(({ label, val, color, icon }, i) => (
              <div key={label} style={{
                padding: '10px 14px',
                borderRight: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                  <span style={{ color }}>{icon}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.35)' }}>
                    {label}
                  </span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color, fontFamily: "'DM Mono', monospace" }}>
                  ₨{parseFloat(val || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
              <span>Budget utilization</span>
              <span style={{ color: pctColor, fontWeight: 800 }}>{pct.toFixed(0)}%</span>
            </div>
            <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 99 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: 99, transition: 'width 0.4s' }} />
            </div>
          </div>

          <div style={{ padding: '6px 14px 10px' }}>
            {loading ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '12px 0' }}>
                Loading...
              </div>
            ) : entries.length === 0 && !adding ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.22)', textAlign: 'center', padding: '10px 0', fontStyle: 'italic' }}>
                No expenses recorded yet.
              </div>
            ) : (
              entries.map((entry, i) => (
                <div key={entry.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 0',
                  borderBottom: i < entries.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#CBD5E1', fontWeight: 600 }}>{entry.description}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 1 }}>
                      {new Date(entry.added_at).toLocaleString('en-GB', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                      {entry.added_by && (
                        <span style={{ marginLeft: 6, color: 'rgba(255,255,255,0.2)' }}>· {entry.added_by}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#FBBF24', fontFamily: "'DM Mono', monospace" }}>
                      ₨{parseFloat(entry.amount).toLocaleString()}
                    </span>
                    <button onClick={() => removeExpense(entry.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'rgba(248,113,113,0.45)', display: 'flex' }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}

            {adding ? (
              <div style={{
                marginTop: 8, background: 'rgba(255,255,255,0.03)',
                borderRadius: 8, padding: 10,
                border: '1px solid rgba(59,130,246,0.18)',
              }}>
                <input
                  placeholder="Expense description (Fuel, Materials...)"
                  style={addInputStyle}
                  value={newItem.description}
                  onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && addExpense()}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input
                    type="number" placeholder="Amount ₨"
                    style={{ ...addInputStyle, flex: 1, fontFamily: "'DM Mono', monospace" }}
                    value={newItem.amount}
                    onChange={e => setNewItem({ ...newItem, amount: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && addExpense()}
                  />
                  <button onClick={addExpense} disabled={saving}
                    style={addBtnStyle('#22D3A5', 'rgba(34,211,165,0.12)')}>
                    <CheckCircle size={13} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => { setAdding(false); setNewItem({ description: '', amount: '' }); }}
                    style={addBtnStyle('#F87171', 'rgba(248,113,113,0.1)')}>
                    <X size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} style={{
                marginTop: 8, width: '100%', padding: '7px 0',
                background: 'rgba(34,211,165,0.05)', border: '1px dashed rgba(34,211,165,0.2)',
                borderRadius: 8, color: '#22D3A5', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                fontFamily: "'DM Sans', sans-serif",
              }}>
                <Plus size={12} /> Log Expense
              </button>
            )}

            {summary.remaining < 0 && (
              <div style={{
                marginTop: 10, padding: '7px 11px', borderRadius: 7,
                background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)',
                fontSize: 11, color: '#F87171', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <AlertTriangle size={12} />
                Expenses exceed approved amount by ₨{Math.abs(summary.remaining).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DYNAMIC SUMMARY CARD
// ─────────────────────────────────────────────────────────────
const PettyCashSummary = ({ recentRequests, ledgerTrigger }) => {
  const [timeFilter, setTimeFilter] = useState('daily');
  const [allExpenses, setAllExpenses] = useState([]);

  const now = new Date();

  useEffect(() => {
    const fetchAllLedgers = async () => {
      const approvedReqs = recentRequests.filter(r => r.status?.toLowerCase() === 'approved');
      if (approvedReqs.length === 0) {
        setAllExpenses([]);
        return;
      }

      const results = await Promise.all(
        approvedReqs.map(r => axios.get(`${API}/job-orders/finance/ledger/${r.id}`).catch(() => null))
      );

      let expensesList = [];
      results.forEach(res => {
        if (res && res.data && res.data.success && res.data.data.entries) {
          expensesList.push(...res.data.data.entries);
        }
      });

      setAllExpenses(expensesList);
    };

    fetchAllLedgers();
  }, [recentRequests, ledgerTrigger]);

  // ✅ FIXED: timezone-safe filter using toLocalDateString helper
  const filterByTime = (dateStr) => {
    if (!dateStr) return false;
    // Use local date string for comparison instead of raw Date object
    const localDateStr = toLocalDateString(dateStr);
    const localDate = new Date(localDateStr); // safe — no time component

    if (timeFilter === 'daily') {
      const todayStr = toLocalDateString(now.toISOString());
      return localDateStr === todayStr;
    } else if (timeFilter === 'weekly') {
      const pastWeek = new Date();
      pastWeek.setDate(now.getDate() - 7);
      pastWeek.setHours(0, 0, 0, 0);
      return localDate >= pastWeek && localDate <= now;
    } else if (timeFilter === 'monthly') {
      return localDate.getMonth() === now.getMonth() && localDate.getFullYear() === now.getFullYear();
    }
    return true;
  };

  const filteredCashReqs = recentRequests.filter(r => r.status?.toLowerCase() === 'approved' && filterByTime(r.requested_at));
  const filteredExpenses = allExpenses.filter(e => filterByTime(e.added_at));

  const totalApproved = filteredCashReqs.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalSpent    = filteredExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const remaining     = totalApproved - totalSpent;

  return (
    <div style={{
      marginBottom: 20, background: '#0D1120',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 14, overflow: 'hidden',
    }}>
      <div style={{
        padding: '13px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 15
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Calendar size={15} color="#F59E0B" />
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>
              Financial Overview
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#E2E8F0' }}>
              Team Spending Summary
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', background: '#141927', padding: '4px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
          {[
            { id: 'daily', label: 'Daily' },
            { id: 'weekly', label: 'Weekly' },
            { id: 'monthly', label: 'Monthly' }
          ].map(f => (
            <button key={f.id} onClick={() => setTimeFilter(f.id)}
              style={{ background: timeFilter === f.id ? '#1F2937' : 'transparent', color: timeFilter === f.id ? '#fff' : 'rgba(255,255,255,0.4)', border: 'none', padding: '6px 16px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {[
          { label: 'Total Approved',  val: totalApproved, color: '#22D3A5', icon: <Wallet size={14} />, sub: `${filteredCashReqs.length} requests in period` },
          { label: 'Total Spent',     val: totalSpent,    color: '#FBBF24', icon: <TrendingDown size={14} />, sub: `${filteredExpenses.length} expenses logged` },
          { label: 'Remaining',       val: remaining,     color: remaining < 0 ? '#F87171' : '#60A5FA', icon: <BarChart2 size={14} />, sub: 'Available Balance' },
        ].map(({ label, val, color, icon, sub }, i) => (
          <div key={label} style={{
            padding: '14px 18px',
            borderRight: i < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ color }}>{icon}</span>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.35)' }}>
                {label}
              </span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>
              ₨{val.toLocaleString()}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>{sub}</div>
          </div>
        ))}
      </div>

      {remaining < 0 && (
        <div style={{
          margin: '0 16px 12px', padding: '7px 12px', borderRadius: 8,
          background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)',
          fontSize: 11, color: '#F87171', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertTriangle size={12} />
          Expenses exceed approved amount by ₨{Math.abs(remaining).toLocaleString()}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const PettyCashForm = () => {
  const [formData, setFormData]               = useState({ jobOrderIds: [], scheduleId: '', teamId: '', amount: '', reason: '', requestedBy: '' });
  const [selectedDate, setSelectedDate]       = useState('');
  const [jobOrders, setJobOrders]             = useState([]);
  const [commercialJobs, setCommercialJobs]   = useState([]);
  const [teams, setTeams]                     = useState([]);
  const [recentRequests, setRecentRequests]   = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [message, setMessage]                 = useState({ type: '', text: '' });
  const [jobType, setJobType]                 = useState('residential');
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [ledgerTrigger, setLedgerTrigger]     = useState(0);

  // ── Dropdowns fetch ──
  useEffect(() => {
    const fetchDropdownData = async () => {
      // Fetch teams
      try {
        const teamRes = await axios.get(`${API}/schedule/teams?active_only=true`);
        const rawTeams = teamRes.data;
        if (Array.isArray(rawTeams)) setTeams(rawTeams);
        else if (rawTeams && Array.isArray(rawTeams.data)) setTeams(rawTeams.data);
        else if (rawTeams && Array.isArray(rawTeams.teams)) setTeams(rawTeams.teams);
        else if (rawTeams && Array.isArray(rawTeams.rows)) setTeams(rawTeams.rows);
        else setTeams([]);
      } catch { setTeams([]); }

      // Fetch residential job orders
      if (jobType === 'residential') {
        try {
          const params = selectedDate ? { specificDate: selectedDate } : {};
          let jobRes;
          try       { jobRes = await axios.get(`${API}/job-orders/ops-job-orders`, { params }); }
          catch     { jobRes = await axios.get(`${API}/active-dropdown`, { params }); }
          const raw = jobRes.data;
          if      (raw && Array.isArray(raw.data)) setJobOrders(raw.data);
          else if (raw && Array.isArray(raw.rows)) setJobOrders(raw.rows);
          else if (Array.isArray(raw))             setJobOrders(raw);
          else setJobOrders([]);
        } catch (err) {
          console.error('Residential fetch error:', err);
          setJobOrders([]);
        }
      }

      // ✅ FIXED: Commercial jobs fetch with timezone-safe date filtering
      if (jobType === 'commercial') {
        try {
          const config = getAuthHeader();
          const params = {};

          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            const clientId = parsedUser.client_id || parsedUser.id;
            if (clientId) params.client_id = clientId;
          }

          const commercialRes = await axios.get(`${API}/schedule/history`, {
            params,
            ...config
          });

          if (commercialRes.data && Array.isArray(commercialRes.data.jobs)) {
            let filteredJobs = commercialRes.data.jobs;

            if (selectedDate) {
              filteredJobs = commercialRes.data.jobs.filter(job => {
                // ✅ KEY FIX: Sabse pehle date field dhundho
                const rawDate =
                  job.scheduled_date ||
                  job.schedule_date  ||
                  job.date           ||
                  job.created_at     ||
                  job.start_date     || '';

                if (!rawDate) return false;

                // ✅ timezone-safe helper se local date string nikalo
                const extractedDate = toLocalDateString(rawDate);
                return extractedDate === selectedDate;
              });
            }

            setCommercialJobs(filteredJobs);
          } else {
            setCommercialJobs([]);
          }
        } catch (err) {
          console.error('Commercial jobs fetch error:', err);
          setCommercialJobs([]);
        }
      }
    };

    fetchDropdownData();
  }, [selectedDate, jobType]);

  // ── History fetch ──
  const fetchRequestHistory = async () => {
    try {
      const res = await axios.get(`${API}/job-orders/finance/petty-cash-all`);
      setRecentRequests(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch request history:', err);
    }
  };
  useEffect(() => { fetchRequestHistory(); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleJobSelection = (jobId) => {
    setFormData(prev => {
      const isSelected = prev.jobOrderIds.includes(jobId);
      return {
        ...prev,
        jobOrderIds: isSelected
          ? prev.jobOrderIds.filter(id => id !== jobId)
          : [...prev.jobOrderIds, jobId]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        ...formData,
        jobType,
        jobOrderId: jobType === 'residential' ? formData.jobOrderIds : undefined,
        scheduleId: jobType === 'commercial'  ? formData.scheduleId  : undefined
      };

      const response = await axios.post(`${API}/job-orders/petty-cash`, payload);
      if (response.data.success || response.status === 201) {
        setMessage({ type: 'success', text: 'Petty cash request submitted successfully!' });
        setFormData({ jobOrderIds: [], scheduleId: '', teamId: '', amount: '', reason: '', requestedBy: '' });
        setSelectedDate('');
        setJobType('residential');
        fetchRequestHistory();
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to submit request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getTeamDisplayName = (savedTeamId) => {
    if (!savedTeamId) return 'Unknown Team';
    if (!isNaN(savedTeamId)) {
      const foundTeam = teams.find(t => String(t.id) === String(savedTeamId));
      if (foundTeam) return `${foundTeam.team_lead_name}'s Team`;
    }
    if (typeof savedTeamId === 'string') {
      if (!savedTeamId.toLowerCase().includes('team')) return `${savedTeamId}'s Team`;
      return savedTeamId;
    }
    return `Team ID: ${savedTeamId}`;
  };

  const getJobName = (jobId, scheduleId) => {
    if (scheduleId) {
      const job = commercialJobs.find(j => (j.schedule_id || j.id) == scheduleId);
      return job ? (job.site_name || job.client_name || `Schedule-${scheduleId}`) : `Schedule-${scheduleId}`;
    }
    if (jobId && String(jobId).includes(',')) {
      return `Multiple Sites (${String(jobId).split(',').length})`;
    }
    const j = jobOrders.find(o => (o.id || o.job_id) == jobId);
    return j ? (j.customer_name || j.client_name || `JO-${jobId}`) : `JO-${jobId}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ── Style tokens ──
  const card = {
    background: '#0D1120', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 14, overflow: 'hidden',
  };
  const cardHeader = {
    padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };
  const cardBody = { padding: '18px 20px' };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  };
  const inputBase = {
    width: '100%', height: 40, background: '#141927',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9,
    color: '#E2E8F0', fontSize: 13, paddingLeft: 36, paddingRight: 12,
    outline: 'none', fontFamily: "'DM Sans', sans-serif",
  };
  const iconWrap = {
    position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
    pointerEvents: 'none', display: 'flex',
  };
  const iconPill = (color) => ({
    width: 36, height: 36, borderRadius: 9,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: color === 'green' ? 'rgba(34,211,165,0.12)' : 'rgba(59,130,246,0.12)',
    border: `1px solid ${color === 'green' ? 'rgba(34,211,165,0.2)' : 'rgba(59,130,246,0.2)'}`,
  });
  const badge = (status) => {
    const map = {
      approved: { bg: 'rgba(34,211,165,0.1)', color: '#22D3A5', border: 'rgba(34,211,165,0.25)' },
      rejected: { bg: 'rgba(248,113,113,0.1)', color: '#F87171', border: 'rgba(248,113,113,0.25)' },
    };
    const s = map[status?.toLowerCase()] || { bg: 'rgba(245,158,11,0.1)', color: '#FBBF24', border: 'rgba(245,158,11,0.25)' };
    return {
      display: 'inline-block', padding: '3px 8px', borderRadius: 5,
      fontSize: 9, fontWeight: 800, letterSpacing: '0.07em',
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    };
  };

  const currentJobList = jobType === 'commercial' ? commercialJobs : jobOrders;
  const currentCount   = currentJobList.length;

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1000, margin: '0 auto', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.5); cursor: pointer; }
        select option { background: #1E2535; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 99px; }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
          Finance Operations
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#E2E8F0', marginTop: 2, letterSpacing: '-0.02em' }}>
          Petty Cash Portal
        </div>
      </div>

      <PettyCashSummary recentRequests={recentRequests} ledgerTrigger={ledgerTrigger} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>

        {/* ── LEFT: FORM ── */}
        <div style={card}>
          <div style={cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={iconPill('green')}><Banknote size={16} color="#22D3A5" /></div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
                  New Request
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#E2E8F0', marginTop: 1 }}>Submit Petty Cash</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              <span style={{ color: '#22D3A5', fontWeight: 700 }}>● </span>Live
            </div>
          </div>

          <div style={cardBody}>
            {message.text && (
              <div style={{
                padding: '9px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 14,
                background: message.type === 'success' ? 'rgba(34,211,165,0.1)' : 'rgba(239,68,68,0.1)',
                color: message.type === 'success' ? '#22D3A5' : '#EF4444',
                border: `1px solid ${message.type === 'success' ? 'rgba(34,211,165,0.25)' : 'rgba(239,68,68,0.25)'}`,
              }}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Job Type Selector */}
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Job Type</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setJobType('residential');
                      setFormData({ ...formData, jobOrderIds: [], scheduleId: '' });
                    }}
                    style={{
                      padding: '10px', borderRadius: 8,
                      background: jobType === 'residential' ? 'rgba(34,211,165,0.15)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${jobType === 'residential' ? '#22D3A5' : 'rgba(255,255,255,0.1)'}`,
                      color: jobType === 'residential' ? '#22D3A5' : 'rgba(255,255,255,0.5)',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                    }}
                  >
                    Residential
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setJobType('commercial');
                      setFormData({ ...formData, jobOrderIds: [], scheduleId: '' });
                    }}
                    style={{
                      padding: '10px', borderRadius: 8,
                      background: jobType === 'commercial' ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${jobType === 'commercial' ? '#3B82F6' : 'rgba(255,255,255,0.1)'}`,
                      color: jobType === 'commercial' ? '#60A5FA' : 'rgba(255,255,255,0.5)',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                    }}
                  >
                    Commercial
                  </button>
                </div>
              </div>

              {/* Date Filter */}
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>
                  Date filter
                  {selectedDate && (
                    <span
                      onClick={() => {
                        setSelectedDate('');
                        setFormData({ ...formData, jobOrderIds: [], scheduleId: '' });
                      }}
                      style={{ fontSize: 10, color: '#EF4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
                    >
                      <X size={11} /> clear
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={iconWrap}><Calendar size={14} color="rgba(255,255,255,0.3)" /></span>
                  <input
                    type="date"
                    style={{ ...inputBase, colorScheme: 'dark' }}
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setFormData({ ...formData, jobOrderIds: [], scheduleId: '' });
                    }}
                  />
                </div>
              </div>

              {/* Job Selection */}
              {jobType === 'residential' ? (
                <div style={{ marginBottom: 14 }}>
                  <div style={labelStyle}>
                    Job Sites (Multiple Selection){' '}
                    <span style={{ color: '#22D3A5', fontWeight: 600 }}>
                      ({formData.jobOrderIds.length} selected)
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Briefcase size={14} color="rgba(255,255,255,0.3)" /></span>
                    <div
                      onClick={() => setShowJobDropdown(!showJobDropdown)}
                      style={{
                        ...inputBase,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingRight: 36
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formData.jobOrderIds.length === 0
                          ? 'Select sites...'
                          : `${formData.jobOrderIds.length} site${formData.jobOrderIds.length > 1 ? 's' : ''} selected`
                        }
                      </span>
                      <ChevronDown size={16} style={{ position: 'absolute', right: 12 }} />
                    </div>

                    {showJobDropdown && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        marginTop: 4, background: '#1E2535',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 9, maxHeight: 240, overflowY: 'auto',
                        zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                      }}>
                        {jobOrders.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
                            No jobs available
                          </div>
                        ) : (
                          jobOrders.map(job => {
                            const id = job.id || job.job_id;
                            const isSelected = formData.jobOrderIds.includes(id);
                            return (
                              <div
                                key={id}
                                onClick={() => toggleJobSelection(id)}
                                style={{
                                  padding: '10px 12px', cursor: 'pointer',
                                  background: isSelected ? 'rgba(34,211,165,0.1)' : 'transparent',
                                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  transition: 'background 0.15s'
                                }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                              >
                                {isSelected
                                  ? <CheckSquare size={16} color="#22D3A5" />
                                  : <Square size={16} color="rgba(255,255,255,0.3)" />
                                }
                                <span style={{
                                  fontSize: 13,
                                  color: isSelected ? '#22D3A5' : '#E2E8F0',
                                  fontWeight: isSelected ? 600 : 400
                                }}>
                                  {job.customer_name || job.client_name || `JO-${id}`}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                  {formData.jobOrderIds.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {formData.jobOrderIds.map(jobId => {
                        const job  = jobOrders.find(j => (j.id || j.job_id) === jobId);
                        const name = job ? (job.customer_name || job.client_name || `JO-${jobId}`) : `JO-${jobId}`;
                        return (
                          <div key={jobId} style={{
                            background: 'rgba(34,211,165,0.15)', border: '1px solid rgba(34,211,165,0.3)',
                            borderRadius: 6, padding: '4px 8px', fontSize: 11, color: '#22D3A5',
                            display: 'flex', alignItems: 'center', gap: 6
                          }}>
                            <span>{name}</span>
                            <X size={12} style={{ cursor: 'pointer' }} onClick={() => toggleJobSelection(jobId)} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <div style={labelStyle}>
                    Commercial Site{' '}
                    <span style={{ color: '#3B82F6', fontWeight: 600 }}>({currentCount})</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Briefcase size={14} color="rgba(255,255,255,0.3)" /></span>
                    <select
                      name="scheduleId"
                      style={{ ...inputBase, cursor: 'pointer', WebkitAppearance: 'none' }}
                      value={formData.scheduleId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{!currentCount ? 'No jobs available...' : 'Select job...'}</option>
                      {commercialJobs.map(job => {
                        const id = job.schedule_id || job.id;
                        return (
                          <option key={id} value={id}>
                            {job.site_name || job.client_name || `Schedule-${id}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}

              <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0 14px' }} />

              {/* Team + Amount */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div>
                  <div style={labelStyle}>Assigned team</div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Users size={14} color="rgba(255,255,255,0.3)" /></span>
                    <select name="teamId" style={{ ...inputBase, cursor: 'pointer', WebkitAppearance: 'none' }}
                      value={formData.teamId} onChange={handleChange} required>
                      <option value="">Select team...</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.team_lead_name}'s Team {t.team_type ? `(${t.team_type})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <div style={labelStyle}>Amount (PKR)</div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ ...iconWrap, color: '#F59E0B', fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>₨</span>
                    <input type="number" name="amount" placeholder="0.00"
                      style={{ ...inputBase, fontFamily: "'DM Mono', monospace", fontSize: 14 }}
                      value={formData.amount} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              {/* Requested By */}
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Requested By</div>
                <div style={{ position: 'relative' }}>
                  <span style={iconWrap}><User size={14} color="rgba(255,255,255,0.3)" /></span>
                  <input type="text" name="requestedBy" placeholder="Your Name..."
                    style={inputBase}
                    value={formData.requestedBy} onChange={handleChange} required />
                </div>
              </div>

              {/* Reason */}
              <div style={{ marginBottom: 20 }}>
                <div style={labelStyle}>
                  Reason / details
                  <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9 }}>optional</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ ...iconWrap, top: 13, transform: 'none' }}>
                    <FileText size={14} color="rgba(255,255,255,0.3)" />
                  </span>
                  <textarea name="reason" placeholder="Fuel, transport, site materials..."
                    style={{ ...inputBase, height: 72, paddingTop: 10, resize: 'vertical', lineHeight: 1.5 }}
                    value={formData.reason} onChange={handleChange} />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || (jobType === 'residential' && formData.jobOrderIds.length === 0)}
                style={{
                  width: '100%', height: 42, borderRadius: 10,
                  background: loading || (jobType === 'residential' && formData.jobOrderIds.length === 0)
                    ? 'rgba(59,130,246,0.5)' : '#3B82F6',
                  border: 'none', color: '#fff', fontWeight: 700, fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  cursor: loading || (jobType === 'residential' && formData.jobOrderIds.length === 0)
                    ? 'not-allowed' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loading ? 'Submitting...' : <><Send size={14} /> Submit Request</>}
              </button>
            </form>
          </div>
        </div>

        {/* ── RIGHT: HISTORY + LEDGER ── */}
        <div style={card}>
          <div style={cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={iconPill('blue')}><Clock size={16} color="#60A5FA" /></div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
                  History
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#E2E8F0', marginTop: 1 }}>My Requests</div>
              </div>
            </div>
            <div style={{
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#60A5FA',
            }}>
              {recentRequests.length} total
            </div>
          </div>

          <div style={{ ...cardBody, paddingTop: 4, maxHeight: 600, overflowY: 'auto' }}>
            {recentRequests.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '32px 0',
                color: 'rgba(255,255,255,0.3)', fontSize: 12,
                border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 10, marginTop: 8,
              }}>
                No requests found.
              </div>
            ) : (
              recentRequests.map((req) => (
                <div key={req.id} style={{ padding: '13px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0', marginBottom: 3 }}>
                        {getJobName(req.job_order_id, req.schedule_id)}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                        <User size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'text-bottom' }} />
                        {getTeamDisplayName(req.team_id)}
                        {req.requested_by && <span style={{ color: '#22D3A5', marginLeft: 4 }}>(By: {req.requested_by})</span>}
                        <span style={{ margin: '0 6px' }}>·</span>
                        {formatTime(req.requested_at)}
                        {req.schedule_id && (
                          <span style={{
                            marginLeft: 6, background: 'rgba(59,130,246,0.2)',
                            padding: '2px 6px', borderRadius: 4, fontSize: 9, color: '#60A5FA'
                          }}>
                            Commercial
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
                        {req.reason ? `"${req.reason}"` : 'No details provided.'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#F59E0B', fontFamily: "'DM Mono', monospace", marginBottom: 5 }}>
                        ₨{req.amount}
                      </div>
                      <span style={badge(req.status)}>
                        {req.status ? req.status.toUpperCase() : 'PENDING'}
                      </span>
                    </div>
                  </div>

                  <LedgerPanel req={req} onLedgerUpdate={() => setLedgerTrigger(t => t + 1)} />
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PettyCashForm;